import {
    useState,
    useRef,
    useMemo,
    useContext,
    useEffect,
    FormEvent,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { Modal } from "antd";
import MultiSelectDropDown from "./MultiSelectDropDown";
import SingleSelectDropDown from "./SingleSelectDropDown";
import {
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
    agDefaultColDef,
    agDefaultSidebar,
} from "@ts/constants";
import { TargetTypeOption } from "@ts/multi-select-dropdown";
import {
    campaignTargetsColDefs,
    campaignsColDefs,
    getTargetData,
    populateCreateCampaigns,
    publishCampaigns,
    onBackButtonClickHandler,
    appendProductsInformation,
} from "@ts/campaign-creation";
import {
    fetchAssetByID,
    resetAssetModal,
    onTargetProductsSelectionChanged,
    createPortfoliosOptions,
    onFileUploadClickHandler,
    autoSizeAll,
} from "@ts/utils";
import { useGetModulesData, usePermissionCheck } from "@hooks/AllCustomHooks";
import { mainContext } from "@ts/constants";
import { AssetUploadModal } from "./AssetUploadModal";
import $ from "jquery";
import ErrorHandler from "@components/error-boundaries/ErrorHandler";
import { useSelector } from "react-redux";
import { GridApi } from "ag-grid-community";
import {
    Campaign,
    IMainContext,
    SelectOptions,
    TProductsSelectionData,
    TSelectedTargets,
    TargetArgs,
} from "@ts/common-types";
import { AiOutlineClose } from "react-icons/ai";

interface ICampaignProps {
    show: boolean;
    setShow: (show: boolean) => void;
    gridApi: GridApi;
}

type CampaignForm = {
    brandName?: string;
    campaignName?: string;
    campaignType?: SelectOptions[];
    fileName?: string;
    matchType?: SelectOptions[];
    targetClass?: SelectOptions[];
    targetType?: SelectOptions[];
};

type CampaignFormErrors = {
    brandName?: string;
    campaignName?: string;
    campaignType?: string;
    fileName?: string;
    matchType?: string;
    targetClass?: string;
    targetType?: string;
};

const CreateCampaignModal: React.FC<ICampaignProps> = ({
    show,
    setShow,
    gridApi,
}) => {
    const {
        targetSelectedNode,
        setTargetSelectedNode,
        assetResponse,
        setAssetResponse,
        isAssetUpload,
        setIsAssetUpload,
        campaignCreationConfigParams,
        setSelectedImageFilter,
        setActivePage,
    } = useContext<IMainContext>(mainContext);

    const [fullscreen] = useState<true | string>(true);
    const campaignsRowData: Campaign[] = [];
    const [targetsRowData, setTargetsRowData] = useState<TSelectedTargets[]>(
        []
    );
    const [portfolioSelected, setPortfolioSelected] = useState();
    const [campaignSelected, setCampaignSelected] = useState([]);
    const [targetSelected, setTargetSelected] = useState([]);
    const [targetClassSelected, setTargetClassSelected] = useState([]);
    const [matchTypeSelected, setMatchTypeSelected] = useState([]);
    const [campaignTargetsApi, setCampaignTargetsApi] = useState<GridApi>();
    const [campaignApi, setCampaignApi] = useState<GridApi>();
    const [charLimit, setCharLimit] = useState(50);
    const fullLength = 1000;
    const [campaignTypeOptions, setCampaignTypeOptions] =
        useState<TargetTypeOption[]>();
    const [portfoliosOptions, setPortfoliosOptions] =
        useState<TargetTypeOption[]>();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [form, setForm] = useState({});
    const [campaignFormErrors, setCampaignFormErrors] = useState<
        Record<string, string>
    >({});
    const [trimmedCampaignName, setTrimmedCampaignName] = useState("");
    const fullLengthStorage = useSelector(
        (state: TProductsSelectionData) => state.productsSelectionData.value
    );

    const { matchTypeOptions, targetTypeOptions } = useGetModulesData({
        type: "dropdown-options",
    });

    const setCampaignField = (field: string, value: string) => {
        setForm((prevState) => ({
            ...prevState,
            [field]: value,
        }));

        if (campaignFormErrors[field])
            setCampaignFormErrors({
                ...errors,
                [field]: null,
            });
    };

    useEffect(() => {
        if (show) {
            if (fullLengthStorage.length) {
                appendProductsInformation(charLimit);
            }
            setCampaignTypeOptions([
                { value: "sp", label: "SP" },
                {
                    value: "sb",
                    label: "SB",
                    isDisabled: !(fullLengthStorage.length >= 3),
                },
                {
                    value: "sbv",
                    label: "SBV",
                    isDisabled: !(fullLengthStorage.length === 1),
                },
                { value: "sd", label: "SD", isDisabled: true },
            ]);

            createPortfoliosOptions(setPortfoliosOptions);
        }
        // eslint-disable-next-line
    }, [charLimit, show]);

    const gridRef = useRef<AgGridReact>(null);
    const onGridReady = () => {
        setCampaignTargetsApi(gridRef.current?.api);
    };

    const handleClose = () => {
        setTargetsRowData([]);
        setCharLimit(50);
        $("#productnamelength").show();
        if ($("#asset_upload_div").is(":visible")) {
            delete (form as CampaignForm)["fileName"];
            setForm(form);
            resetAssetModal(setActivePage);
            setIsAssetUpload(false);
            setErrors({});
        } else {
            setShow(false);
            setTrimmedCampaignName("");
            setCampaignFormErrors({});
        }
    };

    const validateCampaignForm = () => {
        const {
            brandName,
            campaignName,
            campaignType,
            fileName,
            matchType,
            targetClass,
            targetType,
        }: CampaignForm = form;
        const newErrors: CampaignFormErrors = {};

        const selectedCampaigns = campaignSelected.map(({ value }) => value);
        if (selectedCampaigns.includes("sb")) {
            if (!brandName || brandName === "")
                newErrors.brandName = "Please enter a valid brand name";
            if (!fileName || fileName === "")
                newErrors.fileName = "Please select or upload a file";
        }

        if (!campaignName || campaignName === "")
            newErrors.campaignName = "Please enter a valid campaign name";
        if (!campaignType || campaignType.length === 0)
            newErrors.campaignType = "Please select a campaign type";
        if (!matchType || matchType.length === 0)
            newErrors.matchType = "Please select a match type";
        if (!targetClass || targetClass.length === 0)
            newErrors.targetClass = "Please select a target class";
        if (!targetType || targetType.length === 0)
            newErrors.targetType = "Please select a target type";

        return newErrors;
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formErrors = validateCampaignForm();

        if (Object.keys(formErrors).length > 0) {
            setCampaignFormErrors(formErrors);
            const hasCampaignTypeError = "campaignType" in formErrors;
            const hasTargetClassError = "targetClass" in formErrors;
            const hasTargetTypeError = "targetType" in formErrors;
            const hasMatchTypeError = "matchType" in formErrors;

            hasCampaignTypeError
                ? $("#campaignType-validate").show()
                : $("#campaignType-validate").hide();
            hasTargetClassError
                ? $("#targetClass-validate").show()
                : $("#targetClass-validate").hide();
            hasTargetTypeError
                ? $("#targetType-validate").show()
                : $("#targetType-validate").hide();
            hasMatchTypeError
                ? $("#matchType-validate").show()
                : $("#matchType-validate").hide();
        } else {
            const targetArgs: TargetArgs = {
                targetSelected,
                targetClassSelected,
                matchTypeSelected,
                campaignSelected,
                portfolioSelected,
                setTargetsRowData,
                campaignTargetsGridApi: gridApi,
            };

            getTargetData(targetArgs);
        }
    };

    async function onSelectImageBtnClick() {
        let getAssetInfo;
        if ($("#firstStep").hasClass("showing")) {
            const strapiAssetID = sessionStorage.getItem("strapiAssetID");
            getAssetInfo = await fetchAssetByID(strapiAssetID);

            if (getAssetInfo) {
                (
                    document.getElementById("file-name") as HTMLInputElement
                ).value = getAssetInfo.data.attributes.name;
                setCampaignField("fileName", getAssetInfo.data.attributes.name);
                (
                    document.getElementById("file-name") as HTMLInputElement
                ).readOnly = true;
            }
        } else {
            const customAssetMap = sessionStorage.getItem("customAssetMapping")
                ? new Map(
                      Object.entries(
                          JSON.parse(
                              sessionStorage.getItem("customAssetMapping")
                          )
                      )
                  )
                : new Map();

            const campaignCustomImageStrapiAssetID = sessionStorage.getItem(
                "campaignCustomImageStrapiAssetID"
            );

            customAssetMap.set(
                campaignCreationConfigParams.data.name,
                campaignCustomImageStrapiAssetID
            );
            const retrievedNode =
                campaignCreationConfigParams.api.getDisplayedRowAtIndex(
                    campaignCreationConfigParams.node.rowIndex
                );

            const customImageMapObject = Object.fromEntries(customAssetMap);
            sessionStorage.setItem(
                "customAssetMapping",
                JSON.stringify(customImageMapObject)
            );

            getAssetInfo = await fetchAssetByID(
                campaignCustomImageStrapiAssetID
            );
            retrievedNode.setDataValue(
                "asset_name",
                getAssetInfo.data.attributes.name
            );
        }

        setIsAssetUpload(false);
        resetAssetModal(setActivePage);
    }

    const defaultColDef = useMemo(() => {
        return agDefaultColDef;
    }, []);

    const sideBar = useMemo(() => {
        return agDefaultSidebar;
    }, []);

    const HeaderComponent = () => {
        return (
            <div className="d-flex align-items-center pb-3">
                <div
                    className="d-inline"
                    style={{ fontSize: "16px", fontWeight: "400" }}
                >
                    {!isAssetUpload
                        ? "Create New Campaign"
                        : "Select or Upload File"}
                </div>

                <select
                    style={{ fontSize: "0.65rem" }}
                    className="selected-filter badge border bg-light fw-normal text-dark ms-1 mt-1 position-relative d-inline"
                    id="productnamelength"
                    name="productNameLength"
                    defaultValue={charLimit}
                    onChange={(e) => setCharLimit(parseInt(e.target.value))}
                >
                    <option value={fullLength}>Full name</option>
                    <option value={25}>25 characters</option>
                    <option value={50}>50 characters</option>
                    <option value={75}>75 characters</option>
                </select>
            </div>
        );
    };

    const { canListAssets, canCreateAssets } = usePermissionCheck();

    return (
        <>
            <ErrorHandler>
                <Modal
                    title={<HeaderComponent />}
                    open={show}
                    onCancel={handleClose}
                    style={{ top: 25 }}
                    width="95vw"
                    styles={{
                        body: {
                            height: "75vh",
                            overflowY: "auto",
                        },
                    }}
                    closeIcon={<AiOutlineClose style={{ fontSize: "30px" }} />}
                    footer={[
                        <button
                            className="btn btn-primary me-2"
                            id="back"
                            type="button"
                            style={{ display: "none" }}
                            onClick={onBackButtonClickHandler}
                        >
                            Back
                        </button>,
                        <Button
                            className="btn btn-primary me-2"
                            id="createCampaign"
                            style={{ opacity: "0.7", pointerEvents: "none" }}
                            onClick={() =>
                                populateCreateCampaigns(
                                    campaignTargetsApi,
                                    targetSelectedNode
                                )
                            }
                        >
                            Create Campaign
                        </Button>,
                        <Button
                            className="btn btn-primary me-2"
                            id="publish-campaigns"
                            type="button"
                            style={{ display: "none" }}
                            onClick={() =>
                                publishCampaigns(campaignApi, handleClose)
                            }
                        >
                            Publish Campaign
                        </Button>,

                        <span
                            className="content"
                            id="upload-msg"
                            style={{ display: "none" }}
                        >
                            Uploading...
                        </span>,
                        <span
                            className="loader spinner-border spinner-border-sm"
                            id="upload-loader"
                            style={{ display: "none" }}
                        >
                            {" "}
                        </span>,
                        <Button
                            className="btn btn-primary me-2"
                            id="upload-asset"
                            type="submit"
                            form="upload-form"
                            style={{ display: "none" }}
                            disabled={!canCreateAssets}
                        >
                            Upload
                        </Button>,
                        <Button
                            className="btn btn-primary me-2"
                            id="select-asset"
                            type="button"
                            style={{ display: "none" }}
                            onClick={onSelectImageBtnClick}
                            disabled={!canListAssets}
                        >
                            Use Selected Image
                        </Button>,
                        <Button
                            className="btn btn-secondary"
                            type="button"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                        >
                            Close
                        </Button>,
                    ]}
                >
                    <div className="modal-container">
                        <div
                            id="loader-backdrop"
                            style={{
                                display: "none",
                                zoom: "1.5",
                                position: "absolute",
                                width: "100%",
                                zIndex: "10000",
                                height: "100%",
                                background: "rgba(0,0,0 , 0.7)",
                            }}
                        >
                            <i
                                className="spinner-border"
                                style={{
                                    position: "absolute",
                                    top: "45%",
                                    left: "40%",
                                }}
                            ></i>
                        </div>
                        <div className="pb-3 ps-3">
                            <div className="selectedProduct"></div>
                        </div>
                        <div id="firstStep">
                            <Form onSubmit={(event) => handleSubmit(event)}>
                                <Row className="mt-3">
                                    <label className="fw-bold fs-6 pb-4">
                                        {" "}
                                        Campaign Info{" "}
                                    </label>
                                    <Form.Group
                                        as={Col}
                                        md="4"
                                        controlId="campaignname"
                                    >
                                        <Form.Control
                                            type="text"
                                            value={trimmedCampaignName}
                                            onChange={(e) => {
                                                const trimmedName =
                                                    e.target.value.replace(
                                                        /[!#%<>]/g,
                                                        ""
                                                    );
                                                setCampaignField(
                                                    "campaignName",
                                                    trimmedName
                                                );
                                                setTrimmedCampaignName(
                                                    trimmedName
                                                );
                                            }}
                                            isInvalid={
                                                !!campaignFormErrors.campaignName
                                            }
                                            placeholder="Campaign name"
                                        />

                                        <Form.Control.Feedback type="invalid">
                                            {campaignFormErrors.campaignName}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group
                                        as={Col}
                                        md="4"
                                        controlId="portfolioname"
                                        // id="portfolioname"
                                    >
                                        <SingleSelectDropDown
                                            options={portfoliosOptions}
                                            placeholder="Portfolio"
                                            setSelectedOptions={
                                                setPortfolioSelected
                                            }
                                        />
                                    </Form.Group>

                                    <Form.Group
                                        as={Col}
                                        md="4"
                                        controlId="campaigntype"
                                    >
                                        <MultiSelectDropDown
                                            options={campaignTypeOptions}
                                            placeholder="Type of Campaign"
                                            setSelectedOptions={
                                                setCampaignSelected
                                            }
                                            setCampaignField={setCampaignField}
                                            isInvalid={
                                                !!campaignFormErrors.camapaignType
                                            }
                                            type="campaignType"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {campaignFormErrors.campaignType}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Row>
                                <Row
                                    className="mt-3"
                                    id="brand-info"
                                    style={{ display: "none" }}
                                >
                                    <label className="fw-bold fs-6 pb-4">
                                        {" "}
                                        Sponsored Brand Info{" "}
                                    </label>
                                    <Form.Group
                                        as={Col}
                                        md="8"
                                        controlId="brand_name"
                                    >
                                        <Form.Control
                                            type="text"
                                            onChange={(e) => {
                                                setCampaignField(
                                                    "brandName",
                                                    e.target.value
                                                );
                                            }}
                                            isInvalid={
                                                !!campaignFormErrors.brandName
                                            }
                                            placeholder="Brand name"
                                        />

                                        <Form.Control.Feedback type="invalid">
                                            {campaignFormErrors.brandName}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    {/* <Form.Group as={Col} md="4" controlId="sbv_productad">
                                        <Form.Select 
                                            className="form-select"
                                        >
                                            <option disabled value="0">---SBV Product---</option>
                                        </Form.Select>
                                    </Form.Group> */}

                                    <Form.Group as={Col} md="2">
                                        <Button
                                            className="btn btn-dark"
                                            type="button"
                                            id="fileUploadBtn"
                                            onClick={() => {
                                                onFileUploadClickHandler(
                                                    setIsAssetUpload,
                                                    setAssetResponse,
                                                    true,
                                                    setSelectedImageFilter
                                                );
                                            }}
                                        >
                                            Select or Upload Logo
                                        </Button>

                                        <Form.Control
                                            id="file-name"
                                            type="text"
                                            isInvalid={
                                                !!campaignFormErrors.fileName
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {campaignFormErrors.fileName}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Row>
                                <Row className="mt-3">
                                    <label className="fw-bold fs-6 pb-4">
                                        {" "}
                                        Target Info{" "}
                                    </label>
                                    <Form.Group
                                        as={Col}
                                        md="3"
                                        controlId="targetType"
                                    >
                                        <MultiSelectDropDown
                                            options={targetTypeOptions}
                                            placeholder="Target type"
                                            setSelectedOptions={
                                                setTargetSelected
                                            }
                                            setCampaignField={setCampaignField}
                                            isInvalid={
                                                !!campaignFormErrors.targetType
                                            }
                                            type="targetType"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {campaignFormErrors.targetType}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group
                                        as={Col}
                                        md="3"
                                        controlId="targetClass"
                                        // id="targetClass"
                                    >
                                        <MultiSelectDropDown
                                            placeholder="Target class"
                                            setSelectedOptions={
                                                setTargetClassSelected
                                            }
                                            targetSelected={targetSelected}
                                            setCampaignField={setCampaignField}
                                            isInvalid={
                                                !!campaignFormErrors.targetClass
                                            }
                                            type="targetClass"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {campaignFormErrors.targetClass}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group
                                        as={Col}
                                        md="3"
                                        controlId="matchType"
                                        // id="matchType"
                                    >
                                        <MultiSelectDropDown
                                            options={matchTypeOptions}
                                            placeholder="Match Type"
                                            setSelectedOptions={
                                                setMatchTypeSelected
                                            }
                                            setCampaignField={setCampaignField}
                                            isInvalid={
                                                !!campaignFormErrors.matchType
                                            }
                                            type="matchType"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {campaignFormErrors.matchType}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group as={Col} md="3">
                                        <Button
                                            className="btn btn-dark"
                                            type="submit"
                                            id="selecttarget"
                                        >
                                            Select Targets
                                        </Button>
                                    </Form.Group>
                                </Row>
                            </Form>
                            <div
                                className="ag-theme-alpine"
                                id="campaign-targets-grid"
                                // style={{ height: "100%" }}
                            >
                                <AgGridReact
                                    ref={gridRef}
                                    onGridReady={onGridReady}
                                    rowData={targetsRowData}
                                    columnDefs={campaignTargetsColDefs}
                                    defaultColDef={defaultColDef}
                                    sideBar={sideBar}
                                    overlayLoadingTemplate={
                                        overlayLoadingTemplate
                                    }
                                    overlayNoRowsTemplate={
                                        overlayNoRowsTemplate
                                    }
                                    onFirstDataRendered={() => {
                                        autoSizeAll(true, gridRef);
                                    }}
                                    animateRows={true}
                                    suppressRowClickSelection={true}
                                    rowSelection={"multiple"}
                                    onSortChanged={(e) => {
                                        e.api.refreshCells();
                                    }}
                                    onSelectionChanged={(params) =>
                                        onTargetProductsSelectionChanged(
                                            params,
                                            setTargetSelectedNode
                                        )
                                    }
                                ></AgGridReact>
                            </div>
                        </div>
                        <div id="secondStep" style={{ display: "none" }}>
                            <div className="ag-theme-alpine" id="campaign-grid">
                                <AgGridReact
                                    ref={gridRef}
                                    onGridReady={(params) =>
                                        setCampaignApi(params.api)
                                    }
                                    rowData={campaignsRowData}
                                    columnDefs={campaignsColDefs}
                                    defaultColDef={defaultColDef}
                                    sideBar={sideBar}
                                    overlayLoadingTemplate={
                                        overlayLoadingTemplate
                                    }
                                    overlayNoRowsTemplate={
                                        overlayNoRowsTemplate
                                    }
                                    onFirstDataRendered={() => {
                                        autoSizeAll(true, gridRef);
                                    }}
                                    animateRows={true}
                                    suppressRowClickSelection={true}
                                    rowSelection={"multiple"}
                                    rowHeight={80}
                                    onSortChanged={(e) => {
                                        e.api.refreshCells();
                                    }}
                                ></AgGridReact>
                            </div>
                        </div>
                        <div
                            id="asset_upload_div"
                            className="mb-3"
                            style={{ display: "none" }}
                        >
                            <AssetUploadModal
                                assetResponse={assetResponse}
                                errors={errors}
                                setErrors={setErrors}
                                setCampaignField={setCampaignField}
                            />
                        </div>
                    </div>
                </Modal>
            </ErrorHandler>
        </>
    );
};

export { CreateCampaignModal };
